import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, Ticket, Plus, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NotificationBell } from './NotificationBell';

const fmt = (n) => new Intl.NumberFormat('id-ID').format(Math.max(0, Math.floor(Number(n) || 0)));

/**
 * Compact account HUD shown in the top-right of the global header.
 * - Logged in: 🪙 coin · 🎟 ticket · Top Up button + NotificationBell.
 * - Guest: Login / Register CTAs + NotificationBell hidden (handled inside).
 */
export const AccountHUD = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="flex items-center gap-2" data-testid="account-hud-guest">
        <button
          onClick={() => navigate('/login')}
          className="px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5"
          style={{ background: 'rgba(255,255,255,0.06)', color: '#F8FAF9', border: '1px solid rgba(212,175,55,0.35)' }}
          data-testid="hud-login-btn"
        >
          <LogIn className="w-3 h-3" /> Login
        </button>
        <button
          onClick={() => navigate('/register')}
          className="px-4 py-1.5 rounded-full text-xs font-semibold"
          style={{ background: '#D4AF37', color: '#080C0A' }}
          data-testid="hud-register-btn"
        >
          Register
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="account-hud">
      <div
        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full"
        style={{ background: 'rgba(212,175,55,0.10)', border: '1px solid rgba(212,175,55,0.35)' }}
        data-testid="hud-coin"
        title="Saldo Coin"
      >
        <Coins className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} />
        <span className="text-xs font-semibold" style={{ color: '#D4AF37' }} data-testid="hud-coin-value">
          {fmt(user.coin_balance)}
        </span>
        <span className="text-[10px]" style={{ color: '#A3B8AC' }}>Coin</span>
      </div>
      <div
        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
        data-testid="hud-ticket"
        title="Saldo Ticket"
      >
        <Ticket className="w-3.5 h-3.5" style={{ color: '#9CA3AF' }} />
        <span className="text-xs font-semibold" style={{ color: '#F8FAF9' }} data-testid="hud-ticket-value">
          {fmt(user.ticket_balance)}
        </span>
        <span className="text-[10px]" style={{ color: '#A3B8AC' }}>Ticket</span>
      </div>
      <button
        onClick={() => navigate('/topup')}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
        style={{ background: '#D4AF37', color: '#080C0A' }}
        data-testid="hud-topup-btn"
      >
        <Plus className="w-3 h-3" /> Top Up
      </button>
      <NotificationBell />
    </div>
  );
};

/** Mobile-only variant: shows compact balance pill below the header, full-width. */
export const AccountHUDMobile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <div
      className="sm:hidden flex items-center justify-between gap-2 px-4 py-2 mx-3 mt-2 rounded-full"
      style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.25)' }}
      data-testid="account-hud-mobile"
    >
      <div className="flex items-center gap-2 text-xs">
        <span className="flex items-center gap-1" style={{ color: '#D4AF37' }} data-testid="hud-mobile-coin">
          <Coins className="w-3.5 h-3.5" /> {fmt(user.coin_balance)} Coin
        </span>
        <span style={{ color: 'rgba(255,255,255,0.15)' }}>·</span>
        <span className="flex items-center gap-1" style={{ color: '#F8FAF9' }} data-testid="hud-mobile-ticket">
          <Ticket className="w-3.5 h-3.5" /> {fmt(user.ticket_balance)} Ticket
        </span>
      </div>
      <button
        onClick={() => navigate('/topup')}
        className="px-3 py-1 rounded-full text-[11px] font-semibold"
        style={{ background: '#D4AF37', color: '#080C0A' }}
        data-testid="hud-mobile-topup-btn"
      >
        <Plus className="w-3 h-3 inline-block mr-1" /> Top Up
      </button>
    </div>
  );
};
